import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function clientApi() {
  const token = localStorage.getItem("gg_client_token") || sessionStorage.getItem("gg_client_token");
  return axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
}

// URL-safe base64 → Uint8Array for VAPID applicationServerKey
function urlB64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function PushOptIn() {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");

  const refreshStatus = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) return;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (ok) refreshStatus();
  }, [refreshStatus]);

  const subscribe = async () => {
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Permission denied. Enable notifications in browser settings.");
        setLoading(false);
        return;
      }
      const { data: vapid } = await axios.get(`${API}/push/vapid-key`);
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(vapid.publicKey),
      });
      const subJson = sub.toJSON();
      await clientApi().post("/push/subscribe", { endpoint: subJson.endpoint, keys: subJson.keys });
      setSubscribed(true);
      toast.success("Notifications enabled! You'll get updates from your trainer.");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't enable notifications");
    }
    setLoading(false);
  };

  const unsubscribe = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        const subJson = sub.toJSON();
        await clientApi().post("/push/unsubscribe", { endpoint: subJson.endpoint, keys: subJson.keys }).catch(() => {});
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Notifications disabled");
    } catch (err) {
      toast.error("Failed to unsubscribe");
    }
    setLoading(false);
  };

  if (!supported) return null;

  return (
    <AnimatePresence>
      <motion.button
        key={subscribed ? "on" : "off"}
        onClick={subscribed ? unsubscribe : subscribe}
        disabled={loading || permission === "denied"}
        data-testid="push-optin-toggle"
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        whileTap={{ scale: 0.97 }}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-heading uppercase tracking-wider transition-all ${
          subscribed
            ? "border-gg-red/40 bg-gg-red/10 text-gg-red hover:bg-gg-red/15"
            : "border-white/10 text-zinc-400 hover:border-white/20 hover:text-white"
        } disabled:opacity-50`}
        title={permission === "denied" ? "Blocked in browser settings" : subscribed ? "Click to turn off notifications" : "Click to enable notifications"}
      >
        {loading ? <Loader2 size={12} className="animate-spin" /> : subscribed ? <Bell size={12} /> : <BellOff size={12} />}
        <span className="hidden sm:inline">{subscribed ? "Alerts On" : "Enable Alerts"}</span>
      </motion.button>
    </AnimatePresence>
  );
}
